/*
  Attar de Astana · product page

  Three small behaviours, each one optional: the photo dots follow the swipe, the details
  become tabs, and on a phone a buy bar shows once the real buy button has scrolled away.
  The bar's button submits the real product form through its form attribute, so
  product-form.js still handles the add and opens the cart drawer.

  With scripts off every tab panel stays open and the bar is never shown.
*/

(() => {
  const live = new Map();

  const initDots = (root) => {
    const track = root.querySelector('[data-pl-track]');
    const dots = root.querySelector('[data-pl-dots]');
    if (!track || !dots) return () => {};
    const spans = [...dots.children];
    let raf = 0;
    const update = () => {
      raf = 0;
      const i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
      spans.forEach((s, j) => s.classList.toggle('is-on', i === j));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    return () => track.removeEventListener('scroll', onScroll);
  };

  const initTabs = (root) => {
    const tabs = root.querySelector('[data-pl-tabs]');
    const bar = tabs?.querySelector('[data-pl-tabbar]');
    if (!tabs || !bar) return () => {};
    const buttons = [...bar.querySelectorAll('[data-pl-tab]')];
    if (buttons.length === 0) return () => {};

    bar.setAttribute('role', 'tablist');
    const select = (button, focus) => {
      buttons.forEach((b) => {
        const on = b === button;
        const panel = tabs.querySelector(`#${CSS.escape(b.dataset.plTab)}`);
        b.setAttribute('aria-selected', String(on));
        b.tabIndex = on ? 0 : -1;
        if (panel) panel.hidden = !on;
      });
      if (focus) button.focus();
    };

    buttons.forEach((b) => {
      const panel = tabs.querySelector(`#${CSS.escape(b.dataset.plTab)}`);
      b.setAttribute('role', 'tab');
      b.id = `${b.dataset.plTab}-tab`;
      b.setAttribute('aria-controls', b.dataset.plTab);
      if (panel) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', b.id);
        panel.removeAttribute('aria-label');
        panel.tabIndex = 0;
      }
    });

    const onClick = (event) => {
      const button = event.target.closest('[data-pl-tab]');
      if (button) select(button, false);
    };
    const onKey = (event) => {
      const i = buttons.indexOf(document.activeElement);
      if (i < 0) return;
      let next = null;
      if (event.key === 'ArrowRight') next = buttons[(i + 1) % buttons.length];
      if (event.key === 'ArrowLeft') next = buttons[(i - 1 + buttons.length) % buttons.length];
      if (event.key === 'Home') next = buttons[0];
      if (event.key === 'End') next = buttons[buttons.length - 1];
      if (!next) return;
      event.preventDefault();
      select(next, true);
    };

    bar.hidden = false;
    tabs.classList.add('is-ready');
    select(buttons[0], false);
    bar.addEventListener('click', onClick);
    bar.addEventListener('keydown', onKey);

    return () => {
      bar.removeEventListener('click', onClick);
      bar.removeEventListener('keydown', onKey);
    };
  };

  const initBar = (root) => {
    const bar = root.querySelector('[data-pl-bar]');
    const buy = root.querySelector('[data-pl-buy]');
    if (!bar || !buy || !('IntersectionObserver' in window)) return () => {};

    const button = bar.querySelector('[data-pl-bar-button]');
    const real = buy.querySelector('product-form [type="submit"]');

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      bar.classList.toggle('is-shown', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    observer.observe(buy);

    let mirror = null;
    if (button && real) {
      const sync = () => {
        button.disabled = real.hasAttribute('disabled') || real.getAttribute('aria-disabled') === 'true';
        const label = real.querySelector('span')?.textContent.trim();
        const target = button.querySelector('span');
        if (label && target) target.textContent = label;
      };
      mirror = new MutationObserver(sync);
      mirror.observe(real, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'], childList: true, subtree: true, characterData: true });
      sync();
    }

    bar.hidden = false;

    return () => {
      observer.disconnect();
      mirror?.disconnect();
      bar.classList.remove('is-shown');
      bar.hidden = true;
    };
  };

  const init = (root) => {
    if (live.has(root)) return;
    /* product-form.js resets the button to "Add to cart" after a variant change; keep the pre-order label */
    if (root.dataset.preorderLabel && window.variantStrings) {
      window.variantStrings.addToCart = root.dataset.preorderLabel;
    }
    const stops = [initDots(root), initTabs(root), initBar(root)];
    live.set(root, () => stops.forEach((stop) => stop()));
  };

  const stop = (root) => {
    const teardown = live.get(root);
    if (!teardown) return;
    teardown();
    live.delete(root);
  };

  document.querySelectorAll('[data-product-line]').forEach(init);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-product-line]').forEach(init);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-product-line]').forEach(stop);
  });
})();
